import React, {RefObject, useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {GIFT_SHOP_MIN_RESEND_SIZE, GIFT_SHOP_UPLOAD_TYPE} from 'consts'
import {useTranslation} from 'i18n'
import {GiftShopRootScreen, GiftshopTemplateUploadResendModal} from 'pages'
import {
  GiftShopContentType,
  GiftshopResendDataType,
  GiftshopResendModalState,
  GiftshopUploadContentData,
  ObjectState,
} from 'types'
import {
  handleGiftshopReadImage,
  showSnackbar,
  useDebounce,
  useDidUpdate,
  useHistory,
} from 'utils'
import {Button, InputPicker} from 'common/components'
import convertUnit from 'lib/unit'
import {GiftShopResendRevisionContent} from '../containers'

const StyledContainer = styled.div`
  height: 100%;
`

const StyledTryAgain = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(20)};
  margin-top: ${convertUnit(20)};
`

const StyledButton = styled(Button)`
  width: 100%;
`

export default function GiftShopResendRevisionScreenImage({
  collectorId,
  collectorNickname,
  contentId,
  invoiceId,
  username,
  stateRevisionData,
  stateLoadingImg,
  stateLoadingBtn,
  stateSuccess,
  stateResendModal,
  inputRef,
  uploadRevision,
}: {
  collectorNickname: string
  contentId: string
  invoiceId: string
  collectorId: string
  username?: string
  inputRef: RefObject<HTMLInputElement>
  stateRevisionData: ObjectState<GiftshopResendDataType | undefined>
  stateLoadingImg: ObjectState<boolean>
  stateLoadingBtn: ObjectState<boolean>
  stateSuccess: ObjectState<number>
  stateResendModal: ObjectState<GiftshopResendModalState>
  uploadRevision: (
    image: GiftshopUploadContentData,
    contentID: string,
    collectorMemberId: string,
    invoiceID: string,
    contentType: GiftShopContentType,
  ) => Promise<boolean>
}) {
  const {translate} = useTranslation()
  // const {username} = useSelector('user') || {}
  const history = useHistory()
  const [revisionData, setRevisionData] = stateRevisionData
  const setResendModal = stateResendModal[1]
  const [success, setSuccess] = stateSuccess
  const debounce = useDebounce()
  const [loadingImg, setLoadingImg] = stateLoadingImg
  const [loadingBtn, setLoadingBtn] = stateLoadingBtn

  const handleUploadRevision = useCallback(async () => {
    setLoadingBtn(true)
    setResendModal('loading')
    const isSuccess =
      revisionData &&
      (await uploadRevision(
        revisionData.image,
        contentId,
        collectorId,
        invoiceId,
        'photo',
      ))
    if (isSuccess) {
      setSuccess((prev) => prev + 1)
    } else {
      setResendModal('failed')
    }
    setLoadingBtn(false)
  }, [
    collectorId,
    contentId,
    invoiceId,
    revisionData,
    setLoadingBtn,
    setResendModal,
    setSuccess,
    uploadRevision,
  ])

  const handleReadImage = useCallback(
    async (file: File) => {
      if (!GIFT_SHOP_UPLOAD_TYPE.includes(file.type)) {
        showSnackbar(translate('giftShop:invalidFormatData'))
        setLoadingImg(false)
        return
      }

      if (file.size < GIFT_SHOP_MIN_RESEND_SIZE) {
        showSnackbar(translate('giftShop:resendMinSize'))
        setLoadingImg(false)
        return
      }

      const image = await handleGiftshopReadImage(file, {
        checkResolution: false,
      })

      if (image !== null) {
        setLoadingImg(false)
        setRevisionData({id: contentId, image})
      }
    },
    [contentId, setLoadingImg, setRevisionData, translate],
  )

  const handleLoadFiles = useCallback(
    (files: File[]) => {
      setLoadingImg(true)
      Array.from(files).forEach((file) => {
        handleReadImage(file)
      })
    },
    [handleReadImage, setLoadingImg],
  )

  const handleNavigateProfile = useCallback(() => {
    history.replace('giftshop_profile', {self: true}, username)
  }, [history, username])

  const handleRenderContent = useMemo(
    () => (
      <GiftShopResendRevisionContent
        revisionData={revisionData}
        loadingImg={loadingImg}
        onAddPhoto={() => inputRef.current?.click()}
        onUpload={handleUploadRevision}
        loadingBtn={loadingBtn}
      />
    ),
    [handleUploadRevision, inputRef, loadingBtn, loadingImg, revisionData],
  )

  const handleRenderModalBottomSection = useMemo(
    () => (
      <StyledTryAgain>
        <StyledButton
          label={translate('global:close')}
          color="primary_5"
          backgroundColor="white_2"
          onClick={handleNavigateProfile}
        />
        <StyledButton
          label={translate('global:tryAgain')}
          onClick={() => setResendModal('none')}
        />
      </StyledTryAgain>
    ),
    [handleNavigateProfile, setResendModal, translate],
  )

  const handleRenderInputPicker = useMemo(
    () => (
      <InputPicker
        inputRef={inputRef}
        onLoadFiles={handleLoadFiles}
        accept={GIFT_SHOP_UPLOAD_TYPE.join(',')}
      />
    ),
    [handleLoadFiles, inputRef],
  )

  const handleRenderModalResendStatus = useMemo(
    () => (
      <GiftshopTemplateUploadResendModal
        resendModalState={stateResendModal}
        bottomElement={handleRenderModalBottomSection}
        success={success}
        total={1}
      />
    ),
    [handleRenderModalBottomSection, stateResendModal, success],
  )

  useDidUpdate(() => {
    if (success === 1) {
      setResendModal('success')
      debounce(() => {
        handleNavigateProfile()
      }, 500)
    }
  }, [success, history])

  return (
    <GiftShopRootScreen
      showBadgeIcon={false}
      headerTitle={translate('giftShop:resendRevisionButton')}
      headerSubtitle={translate('global:forUser', {
        nickname: collectorNickname,
      })}>
      {handleRenderInputPicker}
      {handleRenderModalResendStatus}
      <StyledContainer>{handleRenderContent}</StyledContainer>
    </GiftShopRootScreen>
  )
}
